'use client';

import { useQuery } from '@tanstack/react-query';
import type { CoPilotContextViewDebugResponse } from '@terra/shared-dto-chat-types';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';
import { getAuthHeaders } from '../../utils/authHeaders';

export const CHAT_CONTEXT_VIEW_QUERY_KEY = 'chat-context-view';

const buildContextViewUrl = ({
  chatId,
  applicationId,
}: {
  chatId: string;
  applicationId: string;
}): string =>
  `${getCurrentGatewayUrl()}/chat/graphs/co-pilot/${chatId}/context-view?applicationId=${encodeURIComponent(applicationId)}`;

export function useChatContextView({
  chatId,
  applicationId,
  enabled = true,
  refetchWhileStreaming = false,
}: {
  chatId: string;
  applicationId?: string;
  enabled?: boolean;
  refetchWhileStreaming?: boolean;
}) {
  return useQuery({
    enabled: enabled && Boolean(chatId) && Boolean(applicationId),
    queryKey: [CHAT_CONTEXT_VIEW_QUERY_KEY, chatId, applicationId],
    queryFn: async (): Promise<CoPilotContextViewDebugResponse> => {
      if (!applicationId) {
        throw new Error('Missing applicationId for context view');
      }

      const authHeaders = await getAuthHeaders();
      const response = await fetch(
        buildContextViewUrl({ chatId, applicationId }),
        {
          credentials: 'include',
          headers: { ...authHeaders },
          method: 'GET',
        },
      );

      if (!response.ok) {
        throw new Error(`Failed to load context view (${response.status})`);
      }

      return (await response.json()) as CoPilotContextViewDebugResponse;
    },
    refetchInterval: refetchWhileStreaming ? 3000 : false,
    staleTime: 1000,
  });
}
